/**
 * Auth Interceptor
 * Authentication ile ilgili özel işlemleri yönetir
 * (Bu dosya request.interceptor.ts'teki AuthTokenInterceptor'dan farklıdır)
 */

import { HttpRequestConfig, HttpResponse } from '../httpClient.interface';
import { AUTH_CONFIG, HTTP_STATUS } from '../../config/api.config';
import { store } from '../../../store';
import { logout, setExpiresAt, setToken } from '../../../store/slices/authSlice';
import type { UserProfile } from '../../types/api.types';

/**
 * Auth state interface
 */
export interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  user: UserProfile | null;
  expiresAt: number | null;
}

/**
 * Token expiration kontrolü
 */
export interface TokenExpirationInfo {
  isExpired: boolean;
  expiresIn: number; // milliseconds
  willExpireSoon: boolean;
}

/**
 * Auth event types
 */
export type AuthEvent = 'login' | 'logout' | 'token_expired' | 'token_refreshed' | 'session_timeout';

/**
 * Auth event listener
 */
export type AuthEventListener = (event: AuthEvent, data?: any) => void;

/**
 * Auth Interceptor - Authentication lifecycle'ını yönetir
 */
export class AuthInterceptor {
  private readonly eventListeners: AuthEventListener[] = [];
  private tokenRefreshPromise: Promise<string> | null = null;
  private lastActivityTimestamp: number = Date.now();
  private inactivityTimer: any = null;
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 dakika

  constructor() {
    this.setupActivityMonitoring();
    this.setupTokenExpirationCheck();
  }

  // ==================== PUBLIC METHODS ====================

  /**
   * Request gönderilmeden önce auth kontrolü yapar
   */
  async beforeRequest(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    // Auth gerekmiyorsa veya skip edilecekse
    if (config.requiresAuth === false || config.skipInterceptors) {
      return config;
    }
  
    // Token expiration kontrolü
    const tokenInfo = await this.checkTokenExpiration();
    
    if (tokenInfo.isExpired) {
      // Token süresi dolmuş, refresh et
      const newToken = await this.refreshToken();
      // Config'i yeni token ile güncelle
      return {
        ...config,
        headers: {
          ...config.headers,
          'Authorization': `Bearer ${newToken}`
        }
      };
    } else if (tokenInfo.willExpireSoon) {
      // Token yakında dolacak, background'da refresh et
      this.refreshTokenInBackground();
    }
  
    // Mevcut token ile config'i güncelle
    const authState = this.getAuthState();
    if (authState.token) {
      return {
        ...config,
        headers: {
          ...config.headers,
          'Authorization': `Bearer ${authState.token}`
        }
      };
    }
  
    // Activity timestamp'ini güncelle
    this.updateActivityTimestamp();
  
    return config;
  }

  /**
   * Response alındıktan sonra auth kontrolü yapar
   */
  async afterResponse(response: HttpResponse): Promise<HttpResponse> {
  // Auth ile ilgili response header'larını kontrol et
  const updatedResponse = this.checkAuthHeaders(response);

  // Activity timestamp'ini güncelle
  this.updateActivityTimestamp();

  return updatedResponse;
}

  /**
   * Error response alındığında auth kontrolü yapar
   */
  async onError(error: any): Promise<any> {
    // 401 Unauthorized hatası
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      await this.handleUnauthorizedError(error);
    }

    // 403 Forbidden hatası
    if (error.response?.status === HTTP_STATUS.FORBIDDEN) {
      this.handleForbiddenError(error);
    }

    return error;
  }

  /**
   * Event listener ekle
   */
  addEventListener(listener: AuthEventListener): void {
    this.eventListeners.push(listener);
  }

  /**
   * Event listener kaldır
   */
  removeEventListener(listener: AuthEventListener): void {
    const index = this.eventListeners.indexOf(listener);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  /**
   * Auth state'i al
   */
  getAuthState(): AuthState {
    const state = store.getState();

    const authState = state.auth as any;
    return {
      isAuthenticated: !!authState?.token,
      token: state.auth?.token || null,
      refreshToken: authState?.refreshToken || 
                    localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN),
      user: authState?.user || null,
      expiresAt: authState?.expiresAt || null
    };
  }

  /**
   * Token'ın geçerlilik süresini kontrol et
   */
  async checkTokenExpiration(): Promise<TokenExpirationInfo> {
    const authState = this.getAuthState();
    
    if (!authState.token || !authState.expiresAt) {
      return {
        isExpired: true,
        expiresIn: 0,
        willExpireSoon: true
      };
    }

    const now = Date.now();
    const expiresAt = authState.expiresAt;
    const expiresIn = expiresAt - now;
    const FIVE_MINUTES = 5 * 60 * 1000;

    return {
      isExpired: expiresIn <= 0,
      expiresIn,
      willExpireSoon: expiresIn > 0 && expiresIn < FIVE_MINUTES
    };
  }

  /**
   * Token refresh et
   */
  async refreshToken(): Promise<string> {
    // Eğer zaten refresh işlemi devam ediyorsa, onun promise'ini dön
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }

    this.tokenRefreshPromise = this.performTokenRefresh();
    
    try {
      const newToken = await this.tokenRefreshPromise;
      this.emitEvent('token_refreshed', { timestamp: Date.now() });
      return newToken;
    } finally {
      this.tokenRefreshPromise = null;
    }
  }

  /**
   * Logout yap
   */
  async logout(): Promise<void> {
    try {
      // Logout API'sini çağır
      await this.performLogout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
    } finally {
      // Local state'i temizle
      this.clearAuthState();
      this.emitEvent('logout');
      
      // Login sayfasına yönlendir
      if (globalThis.location.pathname !== '/login') {
        globalThis.location.href = '/login';
      }
    }
  }

  // ==================== PRIVATE METHODS ====================

  /**
   * Token refresh işlemini gerçekleştir
   */
  private async performTokenRefresh(): Promise<string> {
    const authState = this.getAuthState();
    
    if (!authState.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Refresh token endpoint'ini çağır
      // NOT: Bu endpoint'i kendi backend'ine göre güncelle
      const response = await fetch('/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: authState.refreshToken
        })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      const { accessToken, refreshToken, expiresIn } = data;

      // Yeni token'ları store'a kaydet
      store.dispatch(setToken(accessToken));
      
      if (refreshToken) {
        store.dispatch(setToken(refreshToken));
        localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }

      // Expiration time'ı hesapla ve kaydet
      const expiresAt = Date.now() + (expiresIn * 1000);
      store.dispatch(setExpiresAt(expiresAt));

      // Access token'ı localStorage'a kaydet
      localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, accessToken);

      return accessToken;

    } catch (error) {
      // Refresh başarısızsa logout yap
      await this.logout();
      throw error;
    }
  }

  /**
   * Background'da token refresh et
   */
  private async refreshTokenInBackground(): Promise<void> {
    try {
      await this.refreshToken();
    } catch (error) {
      console.warn('Background token refresh failed:', error);
    }
  }

  /**
   * 401 Unauthorized hatasını handle et
   */
  private async handleUnauthorizedError(error: any): Promise<void> {
    const config = error.config;
    
    // Eğer zaten retry edilmişse
    if (config?._retry) {
      this.emitEvent('token_expired');
      await this.logout();
      return;
    }

    // Token'ı refresh etmeyi dene
    try {
      await this.refreshToken();
      
      // Original request'i yeniden dene
      if (config) {
        config._retry = true;
        // Burada HttpClient'ın request'i tekrar göndermesi gerekir
      }
    } catch (refreshError) {
      // Refresh başarısızsa logout
      this.emitEvent('session_timeout');
      await this.logout();
    }
  }

  /**
   * 403 Forbidden hatasını handle et
   */
  private handleForbiddenError(error: any): void {
    console.warn('Access forbidden:', error);
    
    // Kullanıcıyı bilgilendir
    const event = new CustomEvent('show-toast', {
      detail: {
        type: 'error',
        title: 'Access Denied',
        message: 'You do not have permission to access this resource.',
        duration: 5000
      }
    });
    globalThis.dispatchEvent(event);
  }

  /**
   * Auth state'i temizle
   */
  private clearAuthState(): void {
    store.dispatch(logout());
    
    // LocalStorage'ı temizle
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_CONFIG.STORAGE_KEYS.USER_DATA);
    
    // SessionStorage'ı temizle
    sessionStorage.clear();
    
    // Cookies'i temizle
    this.clearAuthCookies();
  }

  /**
   * Auth cookies'leri temizle
   */
  private clearAuthCookies(): void {
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
      
      // Auth ile ilgili cookie'leri sil
      if (name.includes('token') || name.includes('session') || name.includes('auth')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    }
  }

  /**
   * Response header'larından auth bilgilerini kontrol et
   */
  private checkAuthHeaders(response: HttpResponse): HttpResponse {
    const headers = response.headers;
    let updatedResponse = { ...response };
    
    // Yeni token header'ı varsa güncelle
    const newToken = headers['x-new-access-token'] || headers['x-access-token'];
    if (newToken) {
      store.dispatch(setToken(newToken));
      localStorage.setItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN, newToken);
      
      // Token'ı response'a da ekleyebiliriz
      updatedResponse = {
        ...updatedResponse,
        data: {
          ...updatedResponse.data,
          _newToken: newToken
        }
      };
    }
  
    // Token expiration header'ı varsa güncelle
    const expiresIn = headers['x-token-expires-in'];
    if (expiresIn) {
      const expiresAt = Date.now() + (Number.parseInt(expiresIn) * 1000);
      store.dispatch(setExpiresAt(expiresAt));
    }
  
    // Auth ile ilgili diğer header'lar
    const authHeaders = ['x-auth-status', 'x-user-id', 'x-session-id'];

    for (const header of authHeaders) {
      if (headers[header]) {
        // Header'ı response metadata'sına ekleyebiliriz
        updatedResponse = {
          ...updatedResponse,
          headers: {
            ...updatedResponse.headers,
            [header]: headers[header]
          }
        };
      }
    }
  
    return updatedResponse;
  }

  /**
   * Activity monitoring setup
   */
  private setupActivityMonitoring(): void {
    // User activity event'lerini dinle
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    for (const event of activityEvents) {
      globalThis.addEventListener(event, () => {
        this.updateActivityTimestamp();
        this.resetInactivityTimer();
      }, { passive: true });
    }

    // Inactivity timer'ı başlat
    this.resetInactivityTimer();
  }

  /**
   * Activity timestamp'ini güncelle
   */
  private updateActivityTimestamp(): void {
    this.lastActivityTimestamp = Date.now();
  }

  /**
   * Inactivity timer'ı resetle
   */
  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Inactivity timeout handler
   */
  private handleInactivityTimeout(): void {
    const authState = this.getAuthState();
    
    if (authState.isAuthenticated) {
      console.log('User inactive for 30 minutes, logging out...');
      this.emitEvent('session_timeout');
      this.logout();
    }
  }

  /**
   * Token expiration check setup
   */
  private setupTokenExpirationCheck(): void {
    // Her dakika token expiration'ı kontrol et
    setInterval(async () => {
      const authState = this.getAuthState();
      
      if (authState.isAuthenticated) {
        const tokenInfo = await this.checkTokenExpiration();
        
        if (tokenInfo.willExpireSoon && !tokenInfo.isExpired) {
          // Token yakında dolacak, background'da refresh et
          this.refreshTokenInBackground();
        }
      }
    }, 60 * 1000); // 1 dakika
  }

  /**
   * Logout API'sini çağır
   */
  private async performLogout(): Promise<void> {
    const authState = this.getAuthState();
    
    if (!authState.token) {
      return;
    }

    // Logout endpoint'ini çağır
    await fetch('/auth/logout', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authState.token}`,
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * Event emit et
   */
  private emitEvent(event: AuthEvent, data?: any): void {
    for (const listener of this.eventListeners) {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Auth event listener error:', error);
      }
    }
  }
}

// Singleton instance
export const authInterceptor = new AuthInterceptor();

// Default export
export default authInterceptor;